import React, { useState, useRef, useEffect } from 'react';
import { Menu, ChevronDown, Building2, Users, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWarehouse } from '../../context/WarehouseContext';
import { useClient } from '../../context/ClientContext';
import NotificationBell from '../ui/NotificationBell';

/* Generic badge + dropdown switcher used for both warehouse and client */
const Switcher = ({ icon: Icon, label, open, onToggle, children, interactive }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!interactive) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onToggle(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [interactive, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => interactive && onToggle(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zip-border bg-zip-card transition-colors duration-150
          ${interactive ? 'hover:border-zip-red/40 hover:bg-zip-hover cursor-pointer' : 'cursor-default'}`}
      >
        <Icon size={12} className="text-zip-red flex-shrink-0" />
        <span className="text-[11px] font-mono text-zip-muted tracking-wider">{label}</span>
        {interactive && <ChevronDown size={12} className={`text-zip-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />}
      </button>
      {interactive && open && (
        <div className="absolute right-0 top-full mt-1.5 w-60 bg-zip-surface border border-zip-border rounded-xl shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  );
};

const Header = ({ title, onMenuClick }) => {
  const { user } = useAuth();
  const { warehouses, activeWarehouse, setActiveWarehouse } = useWarehouse();
  const { clients, activeClient, setActiveClient } = useClient();
  const [whOpen, setWhOpen] = useState(false);
  const [clOpen, setClOpen] = useState(false);
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isClientUser = user?.role === 'client_user';

  const whLabel = isAdmin
    ? (activeWarehouse ? `${activeWarehouse.code} · ${activeWarehouse.city}` : 'All Warehouses')
    : (user?.warehouse ? `${user.warehouse.code} · ${user.warehouse.city}` : null);

  const clLabel = isAdmin
    ? (activeClient ? activeClient.name : 'All Clients')
    : isClientUser
      ? (user?.client?.name ?? 'My Account')
      : null;

  return (
    <header className="h-16 flex-shrink-0 flex items-center justify-between px-6 bg-zip-surface border-b border-zip-border">
      {/* Left */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden text-zip-muted hover:text-zip-text transition-colors p-1 focus-ring rounded"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <h1 className="text-[20px] font-semibold text-zip-text tracking-tight">{title}</h1>
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Client switcher */}
        {clLabel && (
          <div className="hidden md:block">
            <Switcher icon={Users} label={clLabel} open={clOpen} onToggle={setClOpen} interactive={isAdmin}>
              <button
                onClick={() => { setActiveClient(null); setClOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-zip-hover transition-colors"
              >
                <Users size={14} className="text-zip-muted flex-shrink-0" />
                <span className="flex-1 text-left font-medium text-zip-text">All Clients</span>
                {!activeClient && <Check size={13} className="text-zip-red" />}
              </button>
              <div className="h-px bg-zip-border mx-3" />
              {clients.map((c) => (
                <button
                  key={c._id}
                  onClick={() => { setActiveClient(c._id); setClOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-zip-hover transition-colors"
                >
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-semibold text-zip-text truncate">{c.name}</div>
                    <div className="text-[11px] font-mono text-zip-muted">{c.code}</div>
                  </div>
                  {activeClient?._id === c._id && <Check size={13} className="text-zip-red flex-shrink-0" />}
                </button>
              ))}
              {clients.length === 0 && <p className="px-4 py-3 text-xs text-zip-muted">No clients yet</p>}
            </Switcher>
          </div>
        )}

        {/* Warehouse switcher */}
        {whLabel && (
          <div className="hidden sm:block">
            <Switcher icon={Building2} label={whLabel} open={whOpen} onToggle={setWhOpen} interactive={isAdmin}>
              <button
                onClick={() => { setActiveWarehouse(null); setWhOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-zip-hover transition-colors"
              >
                <Building2 size={14} className="text-zip-muted flex-shrink-0" />
                <span className="flex-1 text-left font-medium text-zip-text">All Warehouses</span>
                {!activeWarehouse && <Check size={13} className="text-zip-red" />}
              </button>
              <div className="h-px bg-zip-border mx-3" />
              {warehouses.map((wh) => (
                <button
                  key={wh._id}
                  onClick={() => { setActiveWarehouse(wh._id); setWhOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-zip-hover transition-colors"
                >
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-semibold text-zip-text truncate">{wh.name}</div>
                    <div className="text-[11px] font-mono text-zip-muted">{wh.code} · {wh.city}</div>
                  </div>
                  {activeWarehouse?._id === wh._id && <Check size={13} className="text-zip-red flex-shrink-0" />}
                </button>
              ))}
              {warehouses.length === 0 && <p className="px-4 py-3 text-xs text-zip-muted">No warehouses yet</p>}
            </Switcher>
          </div>
        )}

        {/* Notifications */}
        <NotificationBell />

        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-zip-red/20 border border-zip-red/30 flex items-center justify-center flex-shrink-0">
          <span className="text-zip-red text-xs font-bold font-mono">
            {user?.name?.[0]?.toUpperCase() ?? '?'}
          </span>
        </div>
      </div>
    </header>
  );
};

export default Header;
